import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  capitalize,
  getItemBasePrice,
  getOrdinalSuffix,
  getVariantOptionSurchargeForDisplay,
  getVariantTotal,
} from "../../common/util";
import currency from "currency.js";
import Option, { OPTION_GRID_TILE_SX } from "./Option";
import OptionGridCell from "./OptionGridCell";

export default function FlavorSelectionTouch(props) {
  const { fieldKey, variant, fields } = props;
  const selection = variant.size?.value?.settings?.field?.flavor?.selection || "";

  const [flavor, setFlavor] = useState("");
  const [filling, setFilling] = useState("");
  const [selectionRequired, setSelectionRequired] = useState("");
  const [errors, setErrors] = useState([]);

  // Refs for scrolling to next sections
  const flavorRefs = useRef({});
  const fillingRefs = useRef({});

  // Initialize state when component mounts or variant changes
  useEffect(() => {
    setFlavor(variant.flavor || "");
    setFilling(variant.filling || "");

    if (selection === "tier" && variant.size?.value?.tier) {
      let o = { flavor: +variant.size.value.tier || 1 };
      if (fields.filling) {
        o.filling = o.flavor;
      }
      setSelectionRequired(o);
    } else if (selection === "layer") {
      let o = { flavor: +variant.size.value.layer || 1 };
      if (fields.filling) {
        if (o.flavor > 1) {
          o.filling = o.flavor - 1;
        } else {
          o.filling = 1;
        }
      }
      setSelectionRequired(o);
    } else if (selection === "half") {
      let o = { flavor: 2 };
      if (fields.filling) {
        o.filling = 2;
      }
      setSelectionRequired(o);
    } else if (selection === "count") {
      setSelectionRequired({ flavor: +variant.size?.value?.settings?.field?.flavor?.max || 1 });
    }
  }, [variant, selection, fields]);

  const scrollToNext = (currentIndex, type, flavorOverride, fillingOverride) => {
    // Create a flat list of all sections in actual display order
    const allSections = [];

    // Add sections in the order they appear on the page
    const maxIterations = Math.max(selectionRequired.flavor || 0, selectionRequired.filling || 0);
    for (let i = 0; i < maxIterations; i++) {
      if (i < selectionRequired.flavor) {
        allSections.push({ type: "flavor", index: i });
      }
      if (i < selectionRequired.filling) {
        allSections.push({ type: "filling", index: i });
      }
    }

    // Find current section in the list
    const currentSectionIndex = allSections.findIndex(
      (section) => section.type === type && section.index === currentIndex
    );

    // Get next section
    const nextSectionIndex = currentSectionIndex + 1;

    // If we've reached the end, submit the form
    if (nextSectionIndex >= allSections.length) {
      handleSubmit(flavorOverride, fillingOverride);
      return;
    }

    const nextSection = allSections[nextSectionIndex];
    const refKey = `${nextSection.type}-${nextSection.index}`;
    const refs = nextSection.type === "flavor" ? flavorRefs.current : fillingRefs.current;

    if (refs[refKey]) {
      setTimeout(() => {
        refs[refKey].scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }, 300); // Small delay to ensure the UI has updated
    }
  };

  const getTitle = (selection) => {
    if (selection === "tier") {
      return "Flavors by Tier";
    } else if (selection === "layer") {
      return "Flavors by Layer";
    } else if (selection === "half") {
      return "Half & Half Flavors";
    } else if (selection === "count") {
      return "Select Flavors";
    }
  };

  const validate = (flavorOverride, fillingOverride) => {
    let arr = [];
    const flavorVal = flavorOverride !== undefined ? flavorOverride : flavor;
    const fillingVal = fillingOverride !== undefined ? fillingOverride : filling;
    if (selectionRequired.flavor !== Object.values(flavorVal?.value?.parts || "").length) {
      arr.push("Missing flavor");
    }
    if (
      selectionRequired.filling > 0 &&
      selectionRequired.filling !== Object.values(fillingVal?.value?.parts || "").length
    ) {
      arr.push("Missing filling");
    }
    return arr;
  };

  const getFieldLabel = (index, numSelections) => {
    if (numSelections > 1) {
      if (selection === "half") {
        if (index === 0) {
          return "1st Half";
        } else {
          return "2nd Half";
        }
      } else if (selection === "count") {
        return getOrdinalSuffix(+index + 1);
      } else if (selection === "tier") {
        let str = selection + `-${index + 1}`;
        if (index === 0) {
          str += ` (Bottom)`;
        } else if (index === numSelections - 1) {
          str += ` (Top)`;
        }

        return `${capitalize(`${str}`)}`;
      } else if (selection === "layer") {
        let str = selection + `-${index + 1}`;
        return `${capitalize(`${str}`)}`;
      }
    } else {
      return "";
    }
  };

  const handleSubmit = (flavorOverride, fillingOverride) => {
    const arr = validate(flavorOverride, fillingOverride);
    setErrors(arr);

    if (arr.length === 0) {
      props.onChange(
        flavorOverride !== undefined ? flavorOverride : flavor,
        fillingOverride !== undefined ? fillingOverride : filling
      );
    }
  };

  const getNameFromParts = (parts, numSelections) => {
    let arr = [];
    if (Object.keys(parts).length > 1) {
      for (let i = 0; i < Object.keys(parts).length; i++) {
        const part = parts[i].name;

        const title = getFieldLabel(i, numSelections);
        arr.push(`${title}: ${part}`);
      }
    } else {
      arr.push(parts[0].name);
    }

    return arr.join(" • ");
  };

  const sortBySurcharge = (o1, o2) => {
    const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
    const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
    let rslt = +amt1 - +amt2;
    if (rslt === 0) {
      rslt = o1.name.localeCompare(o2.name);
    }

    return rslt;
  };

  const basePrice = props.basePrice || getItemBasePrice(variant);

  const renderFlavorSelection = (index) => {
    const label = getFieldLabel(index, selectionRequired.flavor) + " Flavor";
    const currentValue = flavor?.value?.parts?.[index]?.name || "";

    return (
      <Box
        key={`flavor-${index}`}
        mb={2}
        ref={(el) => {
          if (el) flavorRefs.current[`flavor-${index}`] = el;
        }}
      >
        <Typography variant="h6" mb={2}>
          {label}
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          {fields.flavor.field.options.sort(sortBySurcharge).map((option, idx) => {
            let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);
            const selected = option.name === currentValue;

            return (
              <OptionGridCell gridKey={`flavor-${index}-${option.name ?? "opt"}-${idx}`}>
                <Option
                  price={surcharge}
                  title={option.name}
                  description={""}
                  sx={OPTION_GRID_TILE_SX}
                  onClick={(selected) => {
                    let tmp = { ...flavor };
                    if (!tmp.value) {
                      tmp.value = { selection, parts: {} };
                    }
                    if (selected) {
                      const { override, ...opt } = option;
                      tmp.value.parts[index] = opt;
                    } else {
                      delete tmp.value.parts[index];
                    }
                    tmp.name = getNameFromParts(tmp.value.parts, selectionRequired.flavor);
                    tmp.rank = fields[fieldKey].rank || 1;
                    tmp.pricing = { amount: getVariantTotal(tmp.value.parts, basePrice) };
                    setFlavor(tmp);

                    if (selected) {
                      scrollToNext(index, "flavor", tmp, undefined);
                    }
                  }}
                  selected={selected}
                />
              </OptionGridCell>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderFillingSelection = (index) => {
    const label = getFieldLabel(index, selectionRequired.filling) + " Filling";
    const currentValue = filling?.value?.parts?.[index]?.name || "";

    return (
      <Box
        key={`filling-${index}`}
        mb={3}
        ref={(el) => {
          if (el) fillingRefs.current[`filling-${index}`] = el;
        }}
      >
        <Typography variant="h6" mb={2}>
          {label}
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          {fields.filling?.field?.options?.sort(sortBySurcharge).map((option, idx) => {
            let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);
            const selected = option.name === currentValue;

            return (
              <OptionGridCell gridKey={`filling-${index}-${option.name ?? "opt"}-${idx}`}>
                <Option
                  price={surcharge}
                  title={option.name}
                  description={option.description}
                  sx={OPTION_GRID_TILE_SX}
                  onClick={(selected) => {
                    let tmp = { ...filling };
                    if (!tmp.value) {
                      tmp.value = { selection, parts: {} };
                    }
                    if (selected) {
                      const { override, ...opt } = option;
                      tmp.value.parts[index] = opt;
                    } else {
                      delete tmp.value.parts[index];
                    }
                    tmp.name = getNameFromParts(tmp.value.parts, selectionRequired.filling);
                    tmp.rank = fields[fieldKey].rank || 1;
                    tmp.pricing = { amount: getVariantTotal(tmp.value.parts, basePrice) };
                    setFilling(tmp);

                    if (selected) {
                      scrollToNext(index, "filling", undefined, tmp);
                    }
                  }}
                  selected={selected}
                />
              </OptionGridCell>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box>
        {Array.from(
          { length: Math.max(selectionRequired.flavor || 0, selectionRequired.filling || 0) },
          (_, i) => {
            const elements = [];
            if (i < selectionRequired.flavor) {
              elements.push(renderFlavorSelection(i));
            }
            if (i < selectionRequired.filling) {
              elements.push(renderFillingSelection(i));
            }
            return elements;
          }
        ).flat()}
      </Box>
    </Box>
  );
}
